import type { ComponentType, SVGProps } from "react"
import { useMemo } from "react"
import {
	IconArrowDottedRotateAnticlockwiseOutline18,
	IconCircleDottedOutline18,
	IconCircleUserOutline18,
	IconShapeCircleOutline18,
} from "nucleo-ui-outline-18"

import { SlaIcon } from "@productlane/ui"

import type { InboxState } from "@/lib/states/global-state"
import { THREAD_TAB_LABELS } from "./thread-tab-labels"

type TabIcon = ComponentType<SVGProps<SVGSVGElement>>

export interface ThreadTab {
	value: InboxState
	label: string
	icon: TabIcon
	count?: number
	isActive?: boolean
}

export interface ThreadTabsConfig {
	openCount: number
	newCount: number
	assignedToMeCount: number
	closeLoopCount: number
	slaBreachedCount?: number
	includeSlaBreached?: boolean
	// The combined sidebar lists it at zero too and leaves hiding to its "Show when badged" setting.
	keepEmptySlaBreached?: boolean
	// With auto-send on, Close loop hides while the agent has sent every follow-up.
	includeCloseLoop?: boolean
}

export function useThreadTabs({
	openCount,
	newCount,
	assignedToMeCount,
	closeLoopCount,
	slaBreachedCount = 0,
	includeSlaBreached = false,
	keepEmptySlaBreached = false,
	includeCloseLoop = true,
}: ThreadTabsConfig): ThreadTab[] {
	return useMemo(() => {
		const tabs: ThreadTab[] = []
		if (includeSlaBreached && (keepEmptySlaBreached || slaBreachedCount > 0)) {
			tabs.push({
				value: "sla-breached",
				label: THREAD_TAB_LABELS["sla-breached"],
				icon: SlaIcon,
				count: slaBreachedCount,
			})
		}
		tabs.push(
			{
				value: "new",
				label: THREAD_TAB_LABELS.new,
				icon: IconCircleDottedOutline18,
				count: newCount,
			},
			{
				value: "my",
				label: THREAD_TAB_LABELS.my,
				icon: IconCircleUserOutline18,
				count: assignedToMeCount,
			},
		)
		if (includeCloseLoop) {
			tabs.push({
				value: "close-loop",
				label: THREAD_TAB_LABELS["close-loop"],
				icon: IconArrowDottedRotateAnticlockwiseOutline18,
				count: closeLoopCount,
			})
		}
		tabs.push({
			value: "open",
			label: THREAD_TAB_LABELS.open,
			icon: IconShapeCircleOutline18,
			count: openCount,
		})

		return tabs
	}, [
		openCount,
		newCount,
		assignedToMeCount,
		closeLoopCount,
		slaBreachedCount,
		includeSlaBreached,
		keepEmptySlaBreached,
		includeCloseLoop,
	])
}
